import React, { useState } from "react";
import { useGetVocabularyList } from "@packages/api/madlenAPI";
import { Expand, Book, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import Text from "@core/text";
import { Button } from "@core/index";
import SingleVocabulary from "@features/tools/components/vocabularies/SingleVocabulary";
import { VocabularyListResponseDto } from "@packages/api/madlenAPI.schemas";
import SpinnerLoader from "../components/SpinnerLoader";

interface VocabularyListProps {
  id: number | null;
}

const VocabularyDetailComponent = ({
  tool,
  closeModal,
}: {
  tool: VocabularyListResponseDto;
  closeModal: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <div
      className="fixed inset-0 z-[9999] overflow-hidden bg-black bg-opacity-75 dark:bg-opacity-90 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          closeModal();
        }
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <Text variant={"h1"}>
            {tool.title || t("vocabularies.vocabularyGuide")}
          </Text>
          <Button
            onClick={closeModal}
            variant="solid"
            size="icon"
            rounded="full"
            bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X size={24} className="text-gray-500 dark:text-gray-400" />
          </Button>
        </div>

        <div className="flex-1 overflow-auto p-6 modal-content">
          <SingleVocabulary
            id={tool.id}
            vocabulary={tool.vocabulary ?? []}
            topic={tool.topic}
            title={tool.title}
            description={tool.description}
            imageUrl={tool.imageUrl}
            videoUrl={tool.videoUrl}
            prompt={tool.title || tool.prompt}
            createdAt={tool.createdAt}
            courseName={tool.courseName}
            gradeLevel={tool.gradeLevel}
          />
        </div>
      </div>
    </div>
  );
};

const VocabularyList: React.FC<VocabularyListProps> = ({ id }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading, isError } = useGetVocabularyList(id, {
    query: {
      refetchInterval: (data) => {
        if (
          data?.state.data?.vocabulary &&
          Array.isArray(data?.state.data?.vocabulary) &&
          data?.state.data?.vocabulary.length > 0
        ) {
          return false;
        }
        return 2000;
      },
      enabled: !!id,
      staleTime: Infinity,
      cacheTime: 1000 * 60 * 30,
    },
  });

  const handleExpandClick = () => {
    setExpanded(true);
  };

  const closeModal = () => {
    setExpanded(false);
  };

  const isContentLoading =
    isLoading || !data?.vocabulary || data.vocabulary.length === 0;

  if (isContentLoading) {
    return <SpinnerLoader />;
  }

  if (isError) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm mb-6">
        <h3 className="text-xl font-medium mb-4 text-red-500 dark:text-red-400">
          {t("vocabularies.error.unableToLoad")}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
          {t("vocabularies.error.tryRegenerate")}
        </p>
      </div>
    );
  }

  const vocabularyItems =
    data.vocabulary && Array.isArray(data.vocabulary) ? data.vocabulary : [];

  return (
    <>
      {expanded && data && (
        <VocabularyDetailComponent
          tool={{
            ...data,
            gradeLevel: data.gradeLevel === -1 ? 0 : data.gradeLevel,
          }}
          closeModal={closeModal}
        />
      )}

      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm mb-6">
        <div className="flex w-full justify-between items-center">
          <h3 className="text-xl font-medium mb-4 flex items-center justify-between text-gray-900 dark:text-gray-100">
            <div>{t("vocabularies.vocabularyGuide")}</div>
          </h3>
          <button
            className="flex items-center justify-center p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition duration-200"
            onClick={handleExpandClick}
          >
            <Expand size={18} className="text-gray-400 dark:text-gray-500" />
          </button>
        </div>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
          {t("vocabularies.subtitle")}
        </p>

        <div className="gap-6 flex flex-col">
          {vocabularyItems &&
            vocabularyItems.slice(0, 2).map((word, index) => (
              <div
                key={index}
                className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 overflow-hidden"
              >
                <div className="px-8 py-6">
                  <div>
                    <div className="flex items-center space-x-4 mb-4">
                      <span className="p-2 rounded-xl bg-cyan-100 dark:bg-cyan-900/50 text-cyan-600 dark:text-cyan-400">
                        <Book size={24} />
                      </span>
                      <Text
                        variant={"h2"}
                        className="text-gray-900 dark:text-gray-100"
                      >
                        <Markdown className="prose dark:prose-invert">
                          {word.word}
                        </Markdown>
                      </Text>
                    </div>
                    <Text
                      variant={"p"}
                      size={"base"}
                      className="mb-4 text-gray-700 dark:text-gray-300"
                    >
                      <Markdown className="prose dark:prose-invert">
                        {word.explanation}
                      </Markdown>
                    </Text>
                    {word.usage && (
                      <Text
                        variant={"p"}
                        size={"base"}
                        className="italic !text-cyan-600 dark:!text-cyan-400"
                      >
                        <Markdown className="prose dark:prose-invert">
                          {word.usage}
                        </Markdown>
                      </Text>
                    )}
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </>
  );
};

export default React.memo(VocabularyList);
